import type { ReactNode } from "react"
import { Shield } from "lucide-react"

import { useScopes } from "@/auth/useScopes"

/**
 * Page-level scope gate.
 *
 * The sidebar already hides what a user cannot read, so this catches the other
 * ways into a page: a pasted URL, a bookmark, a link from somebody with more
 * access. It renders an explanation rather than a blank page or a wall of
 * failed requests — a read-only consumer should be able to tell "not for you"
 * from "broken".
 *
 * This is presentation. The backend refuses the underlying request regardless.
 */
export function RequireScope({
  scopes,
  children,
}: {
  /** Every scope the page needs. All of them, not any of them. */
  scopes: string[]
  children: ReactNode
}) {
  const { can } = useScopes()

  if (can(...scopes)) return <>{children}</>

  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <Shield className="w-12 h-12 text-muted-foreground mb-4" />
      <h2 className="text-xl font-semibold text-foreground mb-2">
        You don't have access to this page
      </h2>
      <p className="text-muted-foreground max-w-md">
        Your account is missing {scopes.length > 1 ? "these permissions" : "this permission"}:{" "}
        <code className="font-mono text-foreground">{scopes.join(", ")}</code>.
        Ask an admin if you need it.
      </p>
    </div>
  )
}
